/** Browser-safe contracts. Persistence pointers and private invitation data stay on the server. */
export type CommunityGroupType = 'golf' | 'cycling';
export type CommunityGroupStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
export type MembershipStatus = 'ACTIVE' | 'LEFT' | 'REMOVED';
export type MembershipRole = 'ADMIN' | 'MEMBER';
export type JoinRequestStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'CANCELLED';
export type InvitationStatus = 'PENDING' | 'JOINED' | 'EXPIRED' | 'REVOKED';
export type InvitationDeliveryStatus = 'QUEUED' | 'SENT' | 'FAILED';
export type MyGroupsTab = 'joined' | 'requests' | 'invitations' | 'recommended';

export const FREQUENCIES = [
    { value: 'weekly', label: 'Weekly' }, { value: 'twice_weekly', label: 'Twice a week' },
    { value: 'fortnightly', label: 'Every two weeks' }, { value: 'monthly', label: 'Once a month' },
    { value: 'twice_monthly', label: 'Twice a month' }, { value: 'occasional', label: 'Occasionally' },
    { value: 'custom', label: 'Custom' }
] as const;
export type FrequencyCode = typeof FREQUENCIES[number]['value'];
export const CYCLING_DISCIPLINES = ['Road', 'Mountain Bike', 'BMX', 'Gravel', 'Trail', 'Off-road', 'Track', 'Recreational'] as const;
export const CYCLING_TERRAINS = ['Tar/Road', 'Off-road', 'Mountain', 'Trail', 'Gravel', 'Mixed'] as const;
export const EXPERIENCE_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Professional'] as const;
export type CyclingDiscipline = typeof CYCLING_DISCIPLINES[number];
export type CyclingTerrain = typeof CYCLING_TERRAINS[number];
export type ExperienceLevel = typeof EXPERIENCE_LEVELS[number];

export interface CatalogueItem { id: string; name: string; }
export interface GeographicArea extends CatalogueItem {
    kind: 'COUNTRY' | 'PROVINCE' | 'CITY' | 'AREA';
    parentId?: string;
}
export interface GolfGroupFields { courseIds: string[]; handicapMin?: number; handicapMax?: number; }
export interface CyclingGroupFields {
    experienceLevels: ExperienceLevel[];
    disciplines: CyclingDiscipline[];
    terrains: CyclingTerrain[];
}
export interface CommunityGroupCommonInput {
    name: string;
    categoryId: string;
    description: string;
    logoUrl: string;
    /** Name returned by Parse after a successful file upload. */
    logoFileName?: string;
    areaIds: string[];
    frequencyCode: FrequencyCode;
    frequencyCustom?: string;
    rules?: string;
    membershipRequirements?: string;
}
export type CommunityGroupInput = CommunityGroupCommonInput & (
    { groupType: 'golf'; golf: GolfGroupFields } |
    { groupType: 'cycling'; cycling: CyclingGroupFields }
);
export interface CommunityGroupSummary {
    id: string;
    name: string;
    category: CatalogueItem;
    groupType: CommunityGroupType;
    status: CommunityGroupStatus;
    logoUrl?: string;
    activeMemberCount: number;
    areas: CatalogueItem[];
    frequencyCode: FrequencyCode;
    frequencyCustom?: string;
    context?: string;
}
export interface CommunityGroupDetail extends CommunityGroupSummary {
    logoFileName?: string;
    description: string;
    coverImageUrl?: string;
    rules?: string;
    membershipRequirements?: string;
    golf?: GolfGroupFields & { courses: CatalogueItem[] };
    cycling?: CyclingGroupFields;
    viewer: {
        isSignedIn: boolean;
        isMember: boolean;
        isAdmin: boolean;
        requestStatus?: JoinRequestStatus;
    };
}
export interface Page<T> { items: T[]; nextCursor: string | null; }
export interface GroupListFilter {
    groupType?: CommunityGroupType;
    categoryId?: string;
    frequencyCode?: FrequencyCode;
    name?: string;
    cursor?: string;
    limit?: number;
}
export interface AdminGroupListFilter extends GroupListFilter {
    status?: CommunityGroupStatus;
}
export interface AdminCommunityGroupSummary extends CommunityGroupSummary {
    createdAt?: string;
    administrator: { id: string; name: string };
}
export interface MembershipSnapshot {
    userId: string;
    role: MembershipRole;
    status: MembershipStatus;
}
